/**
 * We need to ensure that every user has a valid profile.
 * With this middleware we check if the user has a profile
 * and cache it on success. Otherwise we try to get the
 * profile data from their user_metadata and set it automatically.
 * If this fails, we redirect the user to a view where they
 * can fill out their profile data manually
 */

import { useQueryClient } from '@tanstack/vue-query';
import type { RouteNamedMap } from 'vue-router/auto-routes';
import { useHasInitProfile } from '~/composables/useHasInitProfile';
import { useMerchantSlug } from '~/composables/useMerchantSlug';
import { useVerifiedSupabaseUser } from '~/composables/useVerifiedSupabaseUser';
import { fetchProfileFn } from '~/queries/useProfileQuery';
import { getQueryDataOrPrefetch } from '~/utils/getQueryDataOrPrefetch';
import { userProfileSchema } from '~~/models/UserProfile';

export default defineNuxtRouteMiddleware(async (to) => {
	const user = useVerifiedSupabaseUser();
	const hasInitProfile = useHasInitProfile();

	if (!user.value) {
		hasInitProfile.value = false;
		return;
	}

	const routesToIgnore: (keyof RouteNamedMap)[] = ['GlobalLogout', 'MerchantLogout', 'MerchantV1Signin', 'GlobalNewPassword'];

	if (routesToIgnore.includes(to.name)) {
		return;
	}

	const queryClient = useQueryClient();
	const queryKey = ['profile'];

	const hasProfile = await getQueryDataOrPrefetch({ queryClient, queryKey, queryFn: fetchProfileFn, ignoreSentry: true })
		.match((profile) => userProfileSchema.safeParse(profile).success, () => false);

	const merchantSlug = useMerchantSlug(to);

	if (hasProfile) {
		hasInitProfile.value = true;

		if (to.name === 'GlobalSignupProfile' || to.name === 'MerchantSignupProfile') {
			if (merchantSlug.value) {
				return navigateTo({ name: 'MerchantDashboard', params: { merchantSlug: merchantSlug.value } });
			}

			return navigateTo('/');
		}

		return;
	}

	hasInitProfile.value = false;

	if (to.name === 'GlobalSignupProfile' || to.name === 'MerchantSignupProfile') {
		return;
	}

	return navigateTo(merchantSlug.value
		? { name: 'MerchantSignupProfile', params: { merchantSlug: merchantSlug.value }, query: { redirectTo: to.fullPath } }
		: { name: 'GlobalSignupProfile', query: { redirectTo: to.fullPath } });
});
